import {
  Button,
  Descriptions,
  Form,
  Input,
  Modal,
  Select,
  Spin,
  Tag
} from "antd"
import { OptionType } from "components/CheckboxList"
import usePlatform from "contexts/platform/use-platform"
import {
  BrokerRoles,
  ICompany,
  IInviteUserRequestBody,
  IOpusUser,
  IUpdateUserRequestBody
} from "platform-client/types"
import { useQuery } from "react-query"
import { useEffect } from "react"

const UserModal: React.FC<IUserModalProps> = ({
  visible,
  isEditing,
  onCancel,
  company,
  user,
  onInviteUser,
  onUpdateUser,
  isLoading,
  isSaving,
  allowedRoles
}) => {
  const [form] = Form.useForm()

  const platform = usePlatform()

  // shares its cache with the Manage Offices modal
  const userBranches = useQuery(
    ["userBranches", user?.id],
    () => platform.user.getUserBranches(user?.id as string),
    { enabled: visible && isEditing && !!user?.id }
  )

  const allOrgRoles = useQuery(
    ["organisationManagement", "listOrgRoles", platform.token],
    () => platform.organizationManagement.listOrgRoles()
  )

  function companyRoles() {
    let orgRoles =
      allOrgRoles.data?.filter((item) =>
        Object.values(BrokerRoles).includes(item.name as BrokerRoles)
      ) || []

    if (allowedRoles && allowedRoles.length > 0) {
      orgRoles = orgRoles?.filter(
        (item) => item.name && allowedRoles.includes(item.name)
      )
    }

    return orgRoles.map(
      (item) =>
        ({
          label: item.name,
          value: item.id
        } as OptionType)
    )
  }

  useEffect(() => {
    if (isEditing && visible && user) {
      form.setFieldsValue(user)

      if (user.roles?.length > 0) {
        form.setFields([{ name: "role", value: user.roles[0]?.id }])
      }
    }
  }, [isEditing, visible, user, form])

  useEffect(() => {
    if (visible) {
      form.resetFields()
    }
  }, [visible, form])

  const handleUpdateUser = (data: IUserForm) => {
    if (!user || !onUpdateUser) {
      return
    }

    onUpdateUser({
      userId: user.id,
      body: {
        firstName: data.firstName,
        lastName: data.lastName,
        phoneNumber: data.phoneNumber,
        userRoles: [data.role]
      }
    })
  }

  const handleInviteUser = (data: IUserForm) => {
    if (!company || !onInviteUser) {
      return
    }

    onInviteUser({
      ...{
        firstName: data.firstName,
        lastName: data.lastName,
        username: data.username,
        phoneNumber: data.phoneNumber
      },
      companyId: company.id,
      companyName: company.name || "",
      userRoles: [data.role]
    })
  }

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      title={isEditing ? "Edit user" : "Add new user"}
      footer={
        <>
          <Button onClick={onCancel} disabled={isSaving || isLoading}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={form.submit}
            disabled={isLoading}
            loading={isSaving}
          >
            {isEditing ? "Save Changes" : "Add User"}
          </Button>
        </>
      }
    >
      <Spin spinning={isLoading}>
        <Form
          form={form}
          onFinish={isEditing ? handleUpdateUser : handleInviteUser}
          name="addUser"
          layout="horizontal"
        >
          <Form.Item style={{ marginBottom: 0 }}>
            <Form.Item
              label={"First name"}
              name="firstName"
              style={{
                display: "inline-block",
                width: "calc(50% - 16px)",
                marginBottom: 10
              }}
              rules={[
                {
                  required: true,
                  message: "Please input first name"
                }
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={"Last name"}
              name="lastName"
              style={{
                display: "inline-block",
                width: "50%",
                margin: "0 0px 0 16px",
                marginBottom: 10
              }}
              rules={[
                {
                  required: true,
                  message: "Please input last name"
                }
              ]}
            >
              <Input />
            </Form.Item>
          </Form.Item>
          <Form.Item
            style={{ marginBottom: 10 }}
            labelCol={{ span: 24 }}
            name="username"
            label={"Email:"}
            rules={[
              {
                required: true,
                message: "Please input email"
              },
              { type: "email", message: "Please input correct email address" }
            ]}
          >
            <Input disabled={isEditing} />
          </Form.Item>
          <Form.Item
            label={"Telephone Number"}
            name="phoneNumber"
            style={{ marginBottom: 10 }}
            labelCol={{ span: 24 }}
            rules={[
              {
                required: true,
                message: "Please input phone number"
              }
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={"Role"}
            name="role"
            style={{ marginBottom: 10 }}
            labelCol={{ span: 24 }}
            rules={[
              {
                required: true,
                message: "Please select a role"
              }
            ]}
          >
            <Select options={companyRoles()} />
          </Form.Item>
          {isEditing && userBranches.data && userBranches.data.length > 0 ? (
            <Form.Item
              label={userBranches.data.length > 1 ? "Offices" : "Office"}
              style={{ marginBottom: 10 }}
              labelCol={{ span: 24 }}
            >
              {userBranches.data.map((branch) => (
                <div key={branch.company.id}>
                  {branch.company.name}
                  {branch.isDefault && userBranches.data.length > 1 && (
                    <Tag style={{ marginLeft: 8 }}>Default</Tag>
                  )}
                </div>
              ))}
            </Form.Item>
          ) : (
            company?.parentCompanyID && (
              <Form.Item
                label={"Office"}
                name="office"
                style={{ marginBottom: 10 }}
                labelCol={{ span: 24 }}
              >
                <Descriptions.Item>
                  {company?.name}
                  <br />
                  {company?.address?.line1}
                  {company?.address?.line2 && `, ${company?.address?.line2}`}
                  {company?.address?.locality &&
                    `, ${company?.address?.locality}`}
                  {company?.address?.postalCode &&
                    `, ${company?.address?.postalCode}`}
                </Descriptions.Item>
              </Form.Item>
            )
          )}
        </Form>
      </Spin>
    </Modal>
  )
}

interface IUserForm {
  firstName: string
  lastName: string
  username: string
  phoneNumber: string
  role: string
}

interface IUserModalProps {
  visible: boolean
  isEditing: boolean
  onCancel: () => void
  onInviteUser?: (body: IInviteUserRequestBody) => void
  onUpdateUser?: (request: IUpdateUserRequest) => void
  isLoading: boolean
  isSaving: boolean
  company: ICompany | undefined
  user?: IOpusUser
  allowedRoles?: string[]
}

export interface IUpdateUserRequest {
  userId: string
  body: IUpdateUserRequestBody
}

export default UserModal
